import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../login/logincss/Login.css";
import logo from "../../images/auravit_logo.png";
import { auth, db } from "../../firebase";
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithPhoneNumber,
  sendEmailVerification,
  updatePassword,
} from "firebase/auth";
import {
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { userService } from "../../services/firestoreService";
import { updateUserEmailByUid } from "../../services/adminService";
import {
  createOtpRequestId,
  generateOtpCode,
  sendOtpEmail,
  sendOtpSms,
  sendPasswordChangeNotification,
  OTP_EXPIRATION_MS,
} from "../../services/otpService";
import { hashPassword } from "../../services/passwordUtils";

// NotificationModal component
function NotificationModal({ message, type, onClose }) {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div
      className={`feedback-modal ${type}`}
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        padding: "12px 20px",
        borderRadius: "8px",
        color: "#fff",
        backgroundColor: type === "success" ? "#36C667" : type === "info" ? "#3498db" : "#e74c3c",
        boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
        zIndex: 9999,
        transition: "opacity 0.3s",
      }}
    >
      {message}
    </div>
  );
}

// EmailSuccessModal component
function EmailSuccessModal({ show, onClose, onLogin }) {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "400px",
          width: "90%",
          textAlign: "center",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div
          style={{
            width: "60px",
            height: "60px",
            backgroundColor: "#36C667",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        
        <h2 style={{ margin: "0 0 16px 0", color: "#333", fontSize: "24px" }}>
          Email Update Successful!
        </h2>
        
        <p style={{ margin: "0 0 24px 0", color: "#666", fontSize: "16px", lineHeight: "1.5" }}>
          Your email has been successfully updated. You need to log in again with your new email address.
        </p>
        
        <button
          onClick={onLogin}
          style={{
            backgroundColor: "#003d82",
            color: "white",
            border: "none",
            padding: "12px 32px",
            borderRadius: "8px",
            fontSize: "16px",
            cursor: "pointer",
            fontWeight: "500",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#002d62")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#003d82")}
        >
          Login
        </button>
      </div>
    </div>
  );
}

export default function ChangePasswordVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const flowType = location.state?.flow || location.state?.type || "login";
  const verificationType = flowType;
  const pendingProfileChange = JSON.parse(
    localStorage.getItem("pendingProfileChange") || "null"
  );
  const pendingAssistedChange = JSON.parse(
    localStorage.getItem("pendingAssistedChange") || "null"
  );
  const isPasswordChangeFlow = location.state?.flow === 'password-change';
  const isAssistedContactChangeFlow = location.state?.flow === 'assisted-contact-change';
  const isAssistedEmailChangeFlow = location.state?.flow === 'assisted-email-change';
  const emailDisplay =
    location.state?.otpEmail ||
    location.state?.email ||
    pendingProfileChange?.updatedData?.email ||
    pendingProfileChange?.currentEmail ||
    // For assisted contact changes, show current user email
    (isAssistedContactChangeFlow ? pendingAssistedChange?.currentUserEmail : null) ||
    // Fallbacks
    pendingAssistedChange?.currentUserEmail ||
    pendingAssistedChange?.updatedData?.email ||
    pendingAssistedChange?.email ||
    "your registered email";
  const userRole = location.state?.role || "user"; // Can be 'user', 'admin', or 'staff'

  // OTP Configuration: 6 digits
  const OTP_LENGTH = 6;

  // Compute profile change variables before state declarations
  const oldEmail = pendingProfileChange?.currentEmail || pendingProfileChange?.originalData?.email || "";
  const newEmail = pendingProfileChange?.updatedData?.email || "";
  const oldContact = pendingProfileChange?.currentContact || pendingProfileChange?.originalData?.contact || "";
  const newContact = pendingProfileChange?.updatedData?.contact || "";
  const normalizedOldContact = String(oldContact || "").replace(/\D/g, "").slice(-10);
  const normalizedNewContact = String(newContact || "").replace(/\D/g, "").slice(-10);
  const hasEmailChange = Boolean(
    oldEmail &&
    newEmail &&
    oldEmail.trim().toLowerCase() !== newEmail.trim().toLowerCase()
  );
  const hasContactChange = Boolean(
    normalizedNewContact &&
    normalizedOldContact &&
    normalizedNewContact !== normalizedOldContact
  );
  const hasProfileChange = hasEmailChange || hasContactChange;

  const pendingPasswordChange = JSON.parse(
    localStorage.getItem("pendingPasswordChange") || "null"
  );

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // OTP expires in 60 seconds
  const [resendText, setResendText] = useState("Resend");
  const [otpExpired, setOtpExpired] = useState(false);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);
  const [profileChangeStep, setProfileChangeStep] = useState("oldEmail");
  const [oldOtpChallenge, setOldOtpChallenge] = useState(null);
  const [newOtpChallenge, setNewOtpChallenge] = useState(null);
  const [smsOtpChallenge, setSmsOtpChallenge] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [phoneAuthInProgress, setPhoneAuthInProgress] = useState(false);
  const [isEmailChanging, setIsEmailChanging] = useState(hasEmailChange);
  const [isContactChanging, setIsContactChanging] = useState(hasContactChange);
  const [dynamicEmailDisplay, setDynamicEmailDisplay] = useState(emailDisplay);
  const otpSentRef = useRef(false);
  const recaptchaVerifierRef = useRef(null);
  const inputsRef = useRef([]);
  // NEW: Verification method (email or sms) for step 1
  const [verificationMethod, setVerificationMethod] = useState(() => {
    // If only contact is changing, default to SMS
    const flowType = location.state?.flow || location.state?.type || "login";
    if (flowType === "profile-change") {
      const pending = JSON.parse(localStorage.getItem("pendingProfileChange") || "null");
      const oldEmail = pending?.currentEmail || pending?.originalData?.email || "";
      const newEmail = pending?.updatedData?.email || "";
      const isEmailChange = newEmail && newEmail !== oldEmail;
      if (!isEmailChange && hasContactChange) {
        return "sms";
      }
    }
    return "email";
  });
  // NEW: Whether we've sent the SMS OTP
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(() => {
    // If only contact is changing and defaulting to SMS, start with false
    const flowType = location.state?.flow || location.state?.type || "login";
    if (flowType === "profile-change") {
      const pending = JSON.parse(localStorage.getItem("pendingProfileChange") || "null");
      const oldEmail = pending?.currentEmail || pending?.originalData?.email || "";
      const newEmail = pending?.updatedData?.email || "";
      const isEmailChange = newEmail && newEmail !== oldEmail;
      if (!isEmailChange && hasContactChange) {
        return false;
      }
    }
    return false;
  });
  // NEW: Whether we've sent the email OTP
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(() => {
    // If it's an email change, start with true (since we auto-send)
    const flowType = location.state?.flow || location.state?.type || "login";
    if (flowType === "profile-change") {
      const pending = JSON.parse(localStorage.getItem("pendingProfileChange") || "null");
      const oldEmail = pending?.currentEmail || pending?.originalData?.email || "";
      const newEmail = pending?.updatedData?.email || "";
      const isEmailChange = newEmail && newEmail !== oldEmail;
      if (isEmailChange) {
        return true;
      }
    }
    return false;
  });
  // NEW: Timer control
  const [timerActive, setTimerActive] = useState(() => {
    // If only contact is changing, don't start timer until Send SMS is clicked
    const flowType = location.state?.flow || location.state?.type || "login";
    if (flowType === "profile-change") {
      const pending = JSON.parse(localStorage.getItem("pendingProfileChange") || "null");
      const oldEmail = pending?.currentEmail || pending?.originalData?.email || "";
      const newEmail = pending?.updatedData?.email || "";
      const isEmailChange = newEmail && newEmail !== oldEmail;
      if (!isEmailChange && hasContactChange) {
        return false;
      }
    }
    return true;
  });
  // NEW: Old phone challenge
  const [oldSmsOtpChallenge, setOldSmsOtpChallenge] = useState(null);
  // NEW: New phone SMS challenge
  const [newSmsOtpChallenge, setNewSmsOtpChallenge] = useState(null);
  // NEW: Password change state
  const [passwordVerificationMethod, setPasswordVerificationMethod] = useState("email");
  const [passwordSmsChallenge, setPasswordSmsChallenge] = useState(null);
  const [passwordHasSmsBeenSent, setPasswordHasSmsBeenSent] = useState(false);
  const [passwordTimerActive, setPasswordTimerActive] = useState(true);

  // Animation state based on timer
  const getTimerState = () => {
    if (otpExpired) return "expired";
    if (timeLeft <= 5) return "critical";
    if (timeLeft <= 15) return "warning";
    return "normal";
  };

  const getCardClasses = () => {
    const baseClass = "forgot-card";
    const timerState = getTimerState();
    
    if (timerState === "expired") return `${baseClass} timer-expired`;
    if (timerState === "critical") return `${baseClass} timer-critical timer-active`;
    if (timeLeft > 0) return `${baseClass} timer-active`;
    return baseClass;
  };

  const getTimerClasses = () => {
    const timerState = getTimerState();
    return `timer timer-${timerState}`;
  };

  // Get old phone number
  const oldPhone = pendingProfileChange?.originalData?.phone || 
                  (pendingProfileChange?.originalData?.phoneLast10 ? `+63${pendingProfileChange.originalData.phoneLast10}` : null) || 
                  pendingProfileChange?.originalData?.contact;
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10 && cleaned.startsWith("9")) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("0")) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith("63")) {
      return `+${cleaned}`;
    } else if (phone.startsWith("+63")) {
      return phone;
    }
    return null;
  };
  const normalizedOldPhone = normalizePhone(oldPhone);
  // Password change phone
  const storedUserForPasswordChange = JSON.parse(localStorage.getItem("user") || "{}");
  const rawPhoneForPassword = storedUserForPasswordChange.phone || storedUserForPasswordChange.contact || (storedUserForPasswordChange.phoneLast10 ? `+63${storedUserForPasswordChange.phoneLast10}` : null);
  const normalizedPhoneForPassword = normalizePhone(rawPhoneForPassword);
  
  const otpEmailDisplay = isAssistedEmailChangeFlow ? dynamicEmailDisplay : (profileChangeStep === "newEmail" ? newEmail || oldEmail : oldEmail || emailDisplay);
  const otpPhoneDisplay = profileChangeStep === "oldEmail" && verificationMethod === "sms" && normalizedOldPhone 
    ? normalizedOldPhone 
    : (normalizedNewContact ? `+63${normalizedNewContact}` : "your mobile number");
  const otpDisplayTarget = profileChangeStep === "oldEmail" && verificationMethod === "sms" 
    ? otpPhoneDisplay 
    : (profileChangeStep === "newPhone" ? otpPhoneDisplay : otpEmailDisplay);

  const sendOtpToEmail = async (email, purpose, saveChallenge) => {
    if (!email) {
      throw new Error("Unable to send OTP. Email is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    await sendOtpEmail({
      email,
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { email, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setError("");
    return challenge;
  };

  const getRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      // Clear existing verifier to avoid conflicts
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {
        console.warn("Error clearing existing reCAPTCHA verifier:", e);
      }
    }
    if (typeof window === "undefined") return null;

    // Ensure reCAPTCHA container exists
    const container = document.getElementById("recaptcha-container");
    if (!container) {
      console.warn("reCAPTCHA container not found in DOM");
      return null;
    }

    try {
      const verifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: (response) => {
            console.log("reCAPTCHA solved successfully");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          }
        },
        auth
      );

      verifier.render().catch((err) => {
        console.warn("reCAPTCHA render failed:", err);
      });

      recaptchaVerifierRef.current = verifier;
      return verifier;
    } catch (error) {
      console.warn("Failed to create reCAPTCHA verifier:", error);
      return null;
    }
  };

  // Function to send SMS OTP using otpService
  const sendOtpToSms = async (phone, purpose, saveChallenge) => {
    if (!phone) {
      throw new Error("Unable to send SMS OTP. Phone number is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    await sendOtpSms({
      phone,
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { phone, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
    setError("");
    return challenge;
  };

  const sendPhoneOtp = async (contact) => {
    if (!contact) {
      throw new Error("Unable to send SMS OTP. Contact number is missing.");
    }

    const normalizedPhone = contact.startsWith("+")
      ? contact
      : `+63${String(contact).replace(/\D/g, "").slice(-10)}`;
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;
    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId("phone-change");

    let verificationId = null;
    let smsTimeout;

    try {
      // Set timeout for SMS sending (15 seconds for Spark Plan)
      smsTimeout = setTimeout(() => {
        console.warn("⏰ SMS sending timed out, using fallback OTP");
        throw new Error("SMS sending timed out. Using fallback OTP method.");
      }, 15000);

      const appVerifier = getRecaptchaVerifier();
      if (!appVerifier) {
        clearTimeout(smsTimeout);
        console.warn("❌ reCAPTCHA verifier not available, using fallback OTP");
        throw new Error("reCAPTCHA verifier could not be initialized. Using fallback OTP.");
      }

      console.log("📡 Sending SMS via Firebase to:", normalizedPhone);
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        normalizedPhone,
        appVerifier
      );
      clearTimeout(smsTimeout);
      verificationId = confirmationResult.verificationId;
      console.log("✅ SMS OTP sent successfully via Firebase to:", normalizedPhone);
    } catch (err) {
      console.warn("❌ SMS OTP Firebase phone auth failed, using fallback OTP:", err.message);
      // Clear any pending timeout
      if (smsTimeout) clearTimeout(smsTimeout);

      // Enhanced user feedback for Spark Plan
      console.log("📱 SMS OTP fallback - Code:", otpCode, "Phone:", normalizedPhone);
      console.log("💡 Note: SMS may be delayed on Spark Plan. Check console for verification code.");
      console.log("🔄 If SMS doesn't arrive, try resending or contact support.");

      // For Spark Plan, we'll still create the challenge but without verificationId
      // The user will need to enter the logged OTP code
    }

    const challenge = {
      phone: normalizedPhone,
      otp: otpCode,
      verificationId,
      requestId,
      expiresAt,
    };

    setSmsOtpChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setError("");

    // Show user-friendly message for SMS status
    if (verificationId) {
      console.log("✅ SMS sent successfully - check your phone for the verification code");
    } else {
      console.log("📝 Using fallback OTP - check console above for the verification code:", otpCode);
      // Show notification to user about fallback
      setNotification({
        message: "SMS verification code shown in console. Please check above for the code.",
        type: "info"
      });
      setTimeout(() => setNotification({ message: "", type: "" }), 8000);
    }

    return challenge;
  };

  const sendCurrentProfileChangeOtp = async () => {
    console.log("🔍 sendCurrentProfileChangeOtp called");
    console.log("pendingProfileChange:", pendingProfileChange);
    console.log("hasProfileChange:", hasProfileChange);
    console.log("oldEmail:", oldEmail);

    if (!pendingProfileChange || !hasProfileChange) {
      console.log("❌ Skipping OTP send - conditions not met");
      return null;
    }

    if (!oldEmail || !oldEmail.includes('@')) {
      console.log("❌ Skipping OTP send - invalid email:", oldEmail);
      return null;
    }

    console.log("📧 Sending OTP to:", oldEmail);
    try {
      const challenge = await sendOtpToEmail(oldEmail, "profile-change-old-email", setOldOtpChallenge);
      console.log("✅ OTP sent successfully to:", oldEmail, "challenge:", challenge);

      return challenge;
    } catch (error) {
      console.error("❌ OTP send failed for:", oldEmail, "error:", error);
      throw error;
    }
  };

  const sendPasswordChangeOtp = async () => {
    const email = location.state?.email || pendingPasswordChange?.email;
    if (!email) {
      throw new Error("Unable to send OTP. Email is missing.");
    }

    const challenge = await sendOtpToEmail(email, "password-change", setOldOtpChallenge);
    return challenge;
  };

  const sendPasswordChangeSmsOtp = async () => {
    if (!normalizedPhoneForPassword) {
      throw new Error("Unable to send OTP. Phone number is missing.");
    }
    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId("password-change-sms");
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    await sendOtpSms({
      phone: normalizedPhoneForPassword,
      otp: otpCode,
      purpose: "password-change",
      requestId,
      expiresAt,
    });

    const challenge = { phone: normalizedPhoneForPassword, otp: otpCode, requestId, expiresAt };
    setPasswordSmsChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setPasswordTimerActive(true);
    setError("");
    return challenge;
  };

  const handlePasswordTryAnotherWay = () => {
    const newMethod = passwordVerificationMethod === "sms" ? "email" : "sms";
    setPasswordVerificationMethod(newMethod);
    if (newMethod === "sms") {
      setPasswordHasSmsBeenSent(false);
    }
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setPasswordTimerActive(false);
    setError("");
  };

  // Monitor auth state changes during OTP process
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user && location.state?.flow === "profile-change") {
        console.warn("⚠️ Auth lost during profile change");

        // STEP 3: Graceful auth failure handling - redirect without showing error

        // Store current progress for potential recovery
        const currentProgress = {
          step: profileChangeStep,
          pendingData: JSON.parse(localStorage.getItem("pendingProfileChange") || "null"),
          timestamp: Date.now()
        };
        sessionStorage.setItem("profileChangeProgress", JSON.stringify(currentProgress));

        // Redirect to login immediately without showing error message
        navigate("/login", {
          state: {
            returnTo: "/user-change-verification",
            resumeProfileChange: true
          }
        });
      }
    });

    return unsubscribe;
  }, [location.state?.flow, profileChangeStep, navigate]);

  // STEP 6: Token health monitoring during multi-step process
  useEffect(() => {
    if (profileChangeStep !== "finalCommit" && location.state?.flow === "profile-change") {
      const healthCheck = setInterval(async () => {
        const healthy = await checkTokenHealth();
        if (!healthy) {
          console.warn("⚠️ Token health check failed during process");
          // Don't interrupt the process here - let the step-by-step checks handle it
        }
      }, 2 * 60 * 1000); // Check every 2 minutes

      return () => clearInterval(healthCheck);
    }
  }, [profileChangeStep, location.state?.flow]);

  // Initialize reCAPTCHA verifier on component mount
  useEffect(() => {
    // Pre-initialize reCAPTCHA verifier for better SMS sending reliability
    const initializeRecaptcha = () => {
      try {
        getRecaptchaVerifier();
        console.log("🔧 reCAPTCHA verifier initialized");
      } catch (error) {
        console.warn("⚠️ Failed to pre-initialize reCAPTCHA:", error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeRecaptcha, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Initialize assisted contact change OTP flow
  useEffect(() => {
    if (!isAssistedContactChangeFlow) {
      console.log("Skipping assisted contact change OTP effect");
      return;
    }

    if (otpSentRef.current) {
      console.log("Skipping assisted contact change OTP effect - already sent in this mount");
      return;
    }

    // Add a small delay to ensure component is fully mounted and data is ready
    const timer = setTimeout(async () => {
      if (otpSentRef.current) return;
      otpSentRef.current = true;

      console.log("Initializing assisted contact change OTP flow");
      
      const pendingAssistedChange = JSON.parse(
        localStorage.getItem("pendingAssistedChange") || "null"
      );
      
      if (!pendingAssistedChange || !pendingAssistedChange.newContact) {
        console.error("No pending assisted change found");
        setError("No pending assisted contact change was found. Please try again.");
        return;
      }

      const currentUserEmail = pendingAssistedChange.currentUserEmail || pendingAssistedChange.updatedData?.email || pendingAssistedChange.email;
      
      if (!currentUserEmail) {
        console.error("Current user email is missing");
        setError("User email is missing. Please try again.");
        return;
      }

      setIsSendingOtp(true);
      setNotification({ message: "Sending verification code to your email...", type: "info" });

      try {
        await sendOtpToEmail(currentUserEmail, "assisted-contact-change", setOldOtpChallenge);
        setNotification({ message: "Verification code sent to your email.", type: "success" });
        console.log("Initial OTP sent successfully for assisted contact change");
      } catch (error) {
        console.error("Failed to send OTP for assisted contact change:", error);
        setError(error.message || "Failed to send verification code. Please try again.");
      } finally {
        setIsSendingOtp(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isAssistedContactChangeFlow]);

  // Initialize assisted email change OTP flow
  useEffect(() => {
    if (!isAssistedEmailChangeFlow) {
      console.log("Skipping assisted email change OTP effect");
      return;
    }

    if (otpSentRef.current) {
      console.log("Skipping assisted email change OTP effect - already sent in this mount");
      return;
    }

    // Add a small delay to ensure component is fully mounted and data is ready
    const timer = setTimeout(async () => {
      if (otpSentRef.current) return;
      otpSentRef.current = true;

      console.log("Initializing assisted email change OTP flow");
      
      const pendingAssistedChange = JSON.parse(
        localStorage.getItem("pendingAssistedChange") || "null"
      );
      
      if (!pendingAssistedChange || !pendingAssistedChange.currentContact) {
        console.error("No pending assisted change found");
        setError("No pending assisted contact change was found. Please try again.");
        return;
      }

      setIsSendingOtp(true);
      setNotification({ message: "Sending verification code to user email...", type: "info" });

      try {
        await sendOtpToEmail(pendingAssistedChange.currentUserEmail, "assisted-contact-old", setOldOtpChallenge);
        setNotification({ message: "Verification code sent to user email.", type: "success" });
        console.log("Initial OTP sent successfully for assisted contact change");
      } catch (error) {
        console.error("Failed to send OTP for assisted email change:", error);
        setError(error.message || "Failed to send verification code. Please try again.");
      } finally {
        setIsSendingOtp(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isAssistedEmailChangeFlow]);

  // Update email display when profileChangeStep changes for assisted email changes
  useEffect(() => {
    if (isAssistedEmailChangeFlow && pendingAssistedChange) {
      let newEmailDisplay;
      if (profileChangeStep === "oldEmail") {
        newEmailDisplay = pendingAssistedChange?.currentEmail;
      } else if (profileChangeStep === "newEmail") {
        newEmailDisplay = pendingAssistedChange?.newEmail;
      } else {
        newEmailDisplay = pendingAssistedChange?.currentEmail;
      }
      setDynamicEmailDisplay(newEmailDisplay || emailDisplay);
    }
  }, [profileChangeStep, isAssistedEmailChangeFlow, pendingAssistedChange, emailDisplay]);

  useEffect(() => {
    if (verificationType !== "profile-change" || !hasProfileChange) {
      console.log("⏭️ Skipping profile change OTP effect", { verificationType, hasProfileChange });
      return;
    }

    if (otpSentRef.current) {
      console.log("⏭️ Skipping profile change OTP effect - already sent in this mount");
      return;
    }

    // Check if it's contact-only change (no email change)
    const pending = JSON.parse(localStorage.getItem("pendingProfileChange") || "null");
    const oldEmailVal = pending?.currentEmail || pending?.originalData?.email || "";
    const newEmailVal = pending?.updatedData?.email || "";
    const isEmailChange = newEmailVal && newEmailVal !== oldEmailVal;
    const isContactOnlyChange = !isEmailChange && hasContactChange;

    if (isContactOnlyChange) {
      console.log("⏭️ Skipping auto-send for contact-only change (user needs to click Send SMS)");
      // Set otpSentRef.current to true to prevent re-entry
      otpSentRef.current = true;
      return;
    }

    // Add a small delay to ensure component is fully mounted and data is ready
    const timer = setTimeout(async () => {
      if (otpSentRef.current) return;
      otpSentRef.current = true;

      console.log("🚀 Initializing profile change OTP flow");
      console.log("verificationType:", verificationType);
      console.log("hasEmailChange:", hasEmailChange, "hasContactChange:", hasContactChange);

      setIsSendingOtp(true);
      setNotification({ message: "Sending verification code to your current email...", type: "info" });

      try {
        const challenge = await sendCurrentProfileChangeOtp();
        if (challenge) {
          setHasEmailBeenSent(true);
          setTimerActive(true);
          setNotification({ message: "Verification code sent to your current email.", type: "success" });
          console.log("✅ Initial OTP sent successfully");
        } else {
          console.log("⚠️ Initial OTP was skipped (conditions not met)");
          setNotification({ message: "", type: "" });
        }
      } catch (err) {
        console.error("❌ Initial OTP send failed:", err);
        setError(err.message || "Unable to send verification code.");
        setNotification({ message: "", type: "" });
      } finally {
        setIsSendingOtp(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [hasProfileChange, verificationType, oldEmail, hasContactChange]);

  // Initialize password change OTP flow
  useEffect(() => {
    if (!isPasswordChangeFlow) return;
    if (otpSentRef.current) return;

    // Add a small delay to ensure component is fully mounted
    const timer = setTimeout(async () => {
      if (otpSentRef.current) return;
      otpSentRef.current = true;

      console.log("🔐 Initializing password change OTP flow");
      setIsSendingOtp(true);
      setNotification({ message: "Sending verification code to your email...", type: "info" });

      try {
        const challenge = await sendPasswordChangeOtp();
        if (challenge) {
          setNotification({ message: "Verification code sent to your email.", type: "success" });
          console.log("✅ Password change OTP sent successfully");
        } else {
          console.log("⚠️ Password change OTP was skipped");
          setNotification({ message: "", type: "" });
        }
      } catch (err) {
        console.error("❌ Password change OTP send failed:", err);
        setError(err.message || "Unable to send verification code.");
        setNotification({ message: "", type: "" });
      } finally {
        setIsSendingOtp(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [isPasswordChangeFlow]);

  // Cleanup for password change flow
  useEffect(() => {
    return () => {
      // Cleanup pending password change if component unmounts during password change flow
      if (isPasswordChangeFlow) {
        const pending = localStorage.getItem('pendingPasswordChange');
        if (pending) {
          // Could add logic to check if user intentionally left
          // For now, keep the pending change for potential recovery
        }
      }
    };
  }, [isPasswordChangeFlow]);

  // STEP 5: Process timing optimization for multi-step changes
  useEffect(() => {
    if (location.state?.flow === "profile-change" && hasProfileChange) {
      // For email + contact changes, log optimization opportunities
      if (profileChangeStep === "oldEmail" && isEmailChanging && isContactChanging) {
        console.log("📋 Multi-step process detected: email + contact change");
        console.log("💡 Optimization: Token refresh between steps enabled");
      }
    }
  }, [profileChangeStep, isEmailChanging, isContactChanging, location.state?.flow, hasProfileChange]);

  // STEP 4: Progress recovery mechanism
  useEffect(() => {
    // Check if resuming from interrupted session
    const savedProgress = sessionStorage.getItem("profileChangeProgress");
    if (savedProgress && location.state?.resumeProfileChange) {
      try {
        const progress = JSON.parse(savedProgress);

        // Validate progress is recent (within 10 minutes)
        if (Date.now() - progress.timestamp < 10 * 60 * 1000) {
          // Restore state
          setProfileChangeStep(progress.step);
          // Note: OTP challenges cannot be restored as they expire
          // User will need to restart from current step

          console.log("✅ Resumed profile change from step:", progress.step);
          sessionStorage.removeItem("profileChangeProgress");
        }
      } catch (error) {
        console.warn("❌ Failed to restore progress:", error);
      }
    }
  }, [location.state?.resumeProfileChange]);

  // Countdown timer logic - OTP expires after 60 seconds
  useEffect(() => {
    const activeTimer = isPasswordChangeFlow ? passwordTimerActive : timerActive;
    if (!activeTimer || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setOtpExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isPasswordChangeFlow, timerActive, passwordTimerActive]);

  // Reset expiration when user requests new OTP
  const resetOtpExpiration = () => {
    setOtpExpired(false);
    setTimeLeft(60);
  };

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Validate OTP is complete (6 digits)
  const isOtpComplete = otp.every((digit) => digit !== "");

  // Handle typing - digits only, auto-focus next box
  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(""); // Clear error when user starts typing

    // Automatically move focus to the next box
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Handle backspace - move to previous box
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle pasting a 6-digit code
  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });

      setOtp(newOtp);
      setError("");
      // Focus the last filled box
      const focusIndex = Math.min(pasteArray.length, OTP_LENGTH - 1);
      inputsRef.current[focusIndex].focus();
    }
    e.preventDefault();
  };

  // Handle cancel button
  const handleCancel = () => {
    localStorage.removeItem("pendingProfileChange");
    // Navigate back to user home with user profile panel loaded
    navigate("/user-home", { state: { activeTab: "profile" } });
  };

  // Try Another Way handler
  const handleTryAnotherWay = () => {
    // Toggle verification method
    const newMethod = verificationMethod === "sms" ? "email" : "sms";
    setVerificationMethod(newMethod);
    if (newMethod === "sms") {
      setHasSmsBeenSent(false);
      setHasEmailBeenSent(true); // keep email sent state if we were on email
    } else {
      setHasEmailBeenSent(false);
      setHasSmsBeenSent(true); // keep sms sent state if we were on sms
    }
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setError("");
  };

  // Send SMS OTP for old phone
  const handleSendSms = async () => {
    if (!normalizedOldPhone) {
      setError("No registered phone number found.");
      return;
    }
    setIsSendingOtp(true);
    setNotification({ message: "Sending verification code to your phone...", type: "info" });
    try {
      await sendOtpToSms(normalizedOldPhone, "profile-change-old-phone", setOldSmsOtpChallenge);
      setHasSmsBeenSent(true);
      setTimerActive(true);
      setNotification({ message: "Verification code sent to your phone.", type: "success" });
      setTimeout(() => setNotification({ message: "", type: "" }), 3000);
    } catch (err) {
      console.error("❌ Failed to send SMS OTP:", err);
      setError(err.message || "Failed to send verification code.");
      setNotification({ message: "", type: "" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Send email OTP for old email
  const handleSendOldEmail = async () => {
    if (!oldEmail) {
      setError("No registered email found.");
      return;
    }
    setIsSendingOtp(true);
    setNotification({ message: "Sending verification code to your email...", type: "info" });
    try {
      await sendOtpToEmail(oldEmail, "profile-change-old-email", setOldOtpChallenge);
      setHasEmailBeenSent(true);
      setTimerActive(true);
      setNotification({ message: "Verification code sent to your email.", type: "success" });
      setTimeout(() => setNotification({ message: "", type: "" }), 3000);
    } catch (err) {
      console.error("❌ Failed to send email OTP:", err);
      setError(err.message || "Failed to send verification code.");
      setNotification({ message: "", type: "" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Send SMS OTP for new phone
  const handleSendNewPhoneSms = async () => {
    if (!newContact) {
      setError("No new phone number provided.");
      return;
    }
    const normalizedNewPhone = `+63${newContact}`;
    setIsSendingOtp(true);
    setNotification({ message: "Sending verification code to your new phone...", type: "info" });
    try {
      await sendOtpToSms(normalizedNewPhone, "profile-change-new-phone", setNewSmsOtpChallenge);
      setHasSmsBeenSent(true);
      setTimerActive(true);
      setNotification({ message: "Verification code sent to your new phone.", type: "success" });
      setTimeout(() => setNotification({ message: "", type: "" }), 3000);
    } catch (err) {
      console.error("❌ Failed to send SMS OTP for new phone:", err);
      setError(err.message || "Failed to send verification code.");
      setNotification({ message: "", type: "" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle resend OTP for old email/sms
  const handleResendOldOtp = async () => {
    if (resendText === "Sent!" || isSendingOtp) return;
    try {
      setIsSendingOtp(true);
      if (verificationMethod === "email") {
        await sendOtpToEmail(oldEmail, "profile-change-old-email", setOldOtpChallenge);
      } else if (verificationMethod === "sms" && normalizedOldPhone) {
        await sendOtpToSms(normalizedOldPhone, "profile-change-old-phone", setOldSmsOtpChallenge);
      }
      setResendText("Sent!");
      setTimeout(() => setResendText("Resend"), 2000);
    } catch (err) {
      console.error("❌ Failed to resend OTP:", err);
      setError(err.message || "Failed to resend verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Validate and refresh token at process start
  const validateAndRefreshToken = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("❌ No authenticated user found");
      return false;
    }

    try {
      // Force token refresh to ensure validity
      const freshToken = await currentUser.getIdToken(true);
      console.log("✅ Token validated and refreshed at process start");

      // Verify the token is actually fresh by checking expiry
      const payload = JSON.parse(atob(freshToken.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const timeToExpiry = expiresAt - Date.now();

      if (timeToExpiry < 10 * 60 * 1000) { // Less than 10 minutes
        console.warn("⚠️ Token expiry too soon after refresh:", Math.round(timeToExpiry/1000/60), "minutes");
        return false;
      }

      return true;
    } catch (error) {
      console.error("❌ Token refresh failed at process start:", error);
      return false;
    }
  };

  // Check token health and refresh if needed
  const checkTokenHealth = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return false;

      const token = await user.getIdToken(false); // Don't force refresh
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const timeToExpiry = expiresAt - Date.now();

      // If token expires within 5 minutes, refresh it
      if (timeToExpiry < 5 * 60 * 1000) {
        console.log("🔄 Proactive token refresh (expires in", Math.round(timeToExpiry/1000/60), "minutes)");
        await user.getIdToken(true);
        return true;
      }

      return true;
    } catch (error) {
      console.warn("❌ Token health check failed:", error);
      return false;
    }
  };

  // Execute final commit after all OTP verifications succeed
  const executeFinalCommit = async (updatePayload, isEmailChanging, isContactChanging, emailValue, contactValue, updatedData, authUser, uid, storedUser, originalData) => {
    console.log("🔄 EXECUTING FINAL COMMIT");
    console.log("Email changing:", isEmailChanging, "Contact changing:", isContactChanging);
    console.log("New email:", emailValue, "New contact:", contactValue);
    console.log("Auth user:", authUser?.uid, "Target UID:", uid);
    console.log("Auth user email:", authUser?.email);

    // FINAL CHECK: Ensure user is still authenticated before any updates
    const currentAuthUser = auth.currentUser;
    if (!currentAuthUser) {
      console.error("❌ User not authenticated at commit time");
      setError("Your session has expired. Please log in again to complete the update.");
      return;
    }

    if (currentAuthUser.uid !== uid) {
      console.error("❌ Auth user mismatch at commit time");
      setError("Authentication error. Please log in again.");
      return;
    }

    // CRITICAL: Always refresh token at start of final commit to prevent expiration during operations
    let freshIdToken = null;
    try {
      console.log("🔄 Refreshing auth token at final commit start...");
      freshIdToken = await currentAuthUser.getIdToken(true);
      console.log("✅ Token refreshed successfully at commit start");
    } catch (tokenError) {
      console.error("❌ Token refresh failed at commit start:", tokenError);
      // Silent logout without showing error message
      navigate("/login");
      return;
    }

    try {
      // VALIDATION: Ensure email/contact are properly prepared
      console.log("📋 UpdatePayload before Firestore:", updatePayload);
      
      if ((isEmailChanging || isContactChanging) && Object.keys(updatePayload).length === 0) {
        console.error("❌ UpdatePayload is empty but changes were expected!");
        setError("Internal error: Changes not properly prepared. Please try again.");
        return;
      }

      if (isEmailChanging && !updatePayload.email) {
        console.error("❌ Email change requested but not in payload");
        setError("Email update data missing. Please try again.");
        return;
      }

      if (isContactChanging && !updatePayload.contact) {
        console.error("❌ Contact change requested but not in payload");
        setError("Contact update data missing. Please try again.");
        return;
      }
      
      // Update Firestore FIRST (before email update to minimize partial update risk)
      console.log("💾 Updating Firestore...");
      await userService.updateUser(uid, updatePayload);
      console.log("✅ Firestore updated successfully");

      // Handle email update with re-authentication (after Firestore succeeds)
      if (isEmailChanging && currentAuthUser && currentAuthUser.uid === uid) {
        console.log("📧 Updating email in Firebase Auth...");
        try {
          // Double-check auth state before making the call
          const finalCheckUser = auth.currentUser;
          if (!finalCheckUser || finalCheckUser.uid !== uid) {
            throw new Error("Authentication lost during update. Please log in again.");
          }

          // Use the fresh token obtained at the start
          await updateUserEmailByUid({ uid, email: emailValue, idToken: freshIdToken });
          console.log("✅ Email updated in Firebase Auth");

          // CRITICAL: Reload user and refresh token to prevent session expiration
          await currentAuthUser.reload();
          await currentAuthUser.getIdToken(true);
          console.log("✅ Auth state refreshed after email update");
        } catch (emailError) {
          console.error("❌ Email update failed:", emailError);
          // CRITICAL: Email update failed after Firestore was updated
          // Try to rollback Firestore changes
          console.log("🔄 Attempting to rollback Firestore changes...");
          try {
            // Revert the Firestore changes
            const revertPayload = {};
            if (isEmailChanging) {
              revertPayload.email = originalData.email;
              revertPayload.emailLower = originalData.email?.toLowerCase();
              revertPayload.verified = originalData.verified || false;
            }
            if (isContactChanging) {
              revertPayload.contact = originalData.contact;
              revertPayload.phone = originalData.phone;
              revertPayload.phoneLast10 = originalData.phoneLast10;
            }
            await userService.updateUser(uid, revertPayload);
            console.log("✅ Firestore changes rolled back");
          } catch (rollbackError) {
            console.error("❌ Failed to rollback Firestore changes:", rollbackError);
          }

          throw new Error(
            `Email update failed after profile was partially updated. Please contact support. ${
              emailError?.message || ""
            }`
          );
        }
      }

      // Handle phone verification and linking (only if we have auth user and verificationId)
      if (isContactChanging && smsOtpChallenge?.verificationId && currentAuthUser) {
        console.log("📱 Linking phone credential...");
        try {
          // Ensure token is still fresh before phone linking
          const phoneLinkToken = await currentAuthUser.getIdToken(true);
          console.log("✅ Token refreshed before phone linking");

          const phoneCredential = PhoneAuthProvider.credential(
            smsOtpChallenge.verificationId,
            otp.join("")
          );
          await linkWithCredential(currentAuthUser, phoneCredential);
          console.log("✅ Phone credential linked");
        } catch (phoneError) {
          console.error("❌ Phone credential linking failed:", phoneError);
          throw new Error(
            phoneError?.message || "Phone verification failed. Please try again."
          );
        }
      }

      // Update local storage and cleanup
      const mergedUser = { ...(storedUser || {}) };
      Object.keys(updatePayload).forEach(key => {
        if (key.includes('.')) {
          const parts = key.split('.');
          let current = mergedUser;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) current[parts[i]] = {};
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = updatePayload[key];
        } else {
          mergedUser[key] = updatePayload[key];
        }
      });
      localStorage.setItem("user", JSON.stringify(mergedUser));
      localStorage.removeItem("pendingProfileChange");

      console.log("Profile update completed successfully");
      
      // Show success modal for email changes, navigate for other changes
      if (isEmailChanging) {
        setShowEmailSuccessModal(true);
      } else {
        setNotification({ message: "Profile updated successfully.", type: "success" });
        setTimeout(() => {
          navigate("/user-home", { state: { user: mergedUser } });
        }, 1000);
      }
    } catch (error) {
      console.error("❌ Final commit failed:", error);
      setError(error.message || "Update failed. Please try again.");
    }
  };

  // Execute assisted contact update after OTP verification
  const executeAssistedContactUpdate = async (pendingAssistedChange, authUser, storedUser) => {
    console.log("EXECUTING ASSISTED CONTACT UPDATE");
    console.log("New contact:", pendingAssistedChange.newContact);
    
    try {
      const { uid, isMinor, fullName, relationship, email, newContact, originalData, updatedData } = pendingAssistedChange;
      const isGrouped = userService.isGroupedStructure(storedUser);
      
      // Build update payload for assisted contact
      const update = {};
      
      if (isMinor) {
        if (isGrouped) {
          update["guardianContactDetails.guardianFullName"] = fullName;
          update["guardianContactDetails.guardianRelationshipToUser"] = relationship;
          update["guardianContactDetails.guardianContactNumber"] = `+63${newContact}`;
        } else {
          update.guardianFullName = fullName;
          update.guardianRelationshipToUser = relationship;
          update.guardianContactNumber = `+63${newContact}`;
        }
      } else {
        if (isGrouped) {
          update["accompanistContactDetails.accompanistFullName"] = fullName;
          update["accompanistContactDetails.accompanistRelationshipToUser"] = relationship;
          update["accompanistContactDetails.accompanistContactNumber"] = `+63${newContact}`;
        } else {
          update.accompanistFullName = fullName;
          update.accompanistRelationshipToUser = relationship;
          update.accompanistContactNumber = `+63${newContact}`;
        }
      }

      // Update Firestore
      await updateDoc(doc(db, "users", uid), {
        ...update,
        updatedAt: serverTimestamp(),
      });

      // Update local storage and state
      const mergedUser = { ...(storedUser || {}) };
      Object.keys(update).forEach(key => {
        if (key.includes('.')) {
          const parts = key.split('.');
          let current = mergedUser;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) current[parts[i]] = {};
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = update[key];
        } else {
          mergedUser[key] = update[key];
        }
      });
      
      localStorage.setItem("user", JSON.stringify(mergedUser));
      localStorage.removeItem("pendingAssistedChange");

      console.log("Assisted contact update completed successfully");
      setNotification({ message: "Assisted contact information updated successfully!", type: "success" });
      setTimeout(() => {
        navigate("/user-home", { state: { user: mergedUser } });
      }, 1000);
    } catch (error) {
      console.error("Assisted contact update failed:", error);
      setError(error.message || "Failed to update assisted contact information.");
    }
  };

  // Execute assisted email update after OTP verification
  const executeAssistedEmailUpdate = async (pendingAssistedChange, authUser, storedUser) => {
    console.log("EXECUTING ASSISTED EMAIL UPDATE");
    console.log("New email:", pendingAssistedChange.newEmail);
    
    try {
      const { uid, isMinor, fullName, relationship, currentEmail, newEmail, originalData, updatedData, changeType } = pendingAssistedChange;
      const isGrouped = userService.isGroupedStructure(storedUser);
      
      // Build update payload for assisted email
      const update = {};
      
      if (isMinor) {
        if (isGrouped) {
          update["guardianContactDetails.guardianFullName"] = fullName;
          update["guardianContactDetails.guardianRelationshipToUser"] = relationship;
          if (changeType === "both" || changeType === "contact") {
            update["guardianContactDetails.guardianContactNumber"] = `+63${updatedData.contact}`;
          }
        } else {
          update.guardianFullName = fullName;
          update.guardianRelationshipToUser = relationship;
          if (changeType === "both" || changeType === "contact") {
            update.guardianContactNumber = `+63${updatedData.contact}`;
          }
        }
      }

      // Update Firestore
      await updateDoc(doc(db, "users", uid), {
        ...update,
        updatedAt: serverTimestamp(),
      });

      // Update local storage and state
      const mergedUser = { ...(storedUser || {}) };
      Object.keys(update).forEach(key => {
        if (key.includes('.')) {
          const parts = key.split('.');
          let current = mergedUser;
          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) current[parts[i]] = {};
            current = current[parts[i]];
          }
          current[parts[parts.length - 1]] = update[key];
        } else {
          mergedUser[key] = update[key];
        }
      });
      
      localStorage.setItem("user", JSON.stringify(mergedUser));
      localStorage.removeItem("pendingAssistedChange");

      console.log("Assisted contact update completed successfully");
      setNotification({ message: "Guardian contact updated successfully!", type: "success" });
      setTimeout(() => {
        navigate("/user-home", { state: { user: mergedUser } });
      }, 1000);
    } catch (error) {
      console.error("Assisted email update failed:", error);
      setError(error.message || "Failed to update guardian contact.");
    }
  };

  // Handle OTP verification - UPDATED FOR MULTIPLE FLOWS
  const handleVerify = async () => {
    console.log("🔍 handleVerify called, profileChangeStep:", profileChangeStep);
    // Validate OTP is complete
    if (!isOtpComplete) {
      setError("Please enter the full 6-digit code");
      // Reset OTP boxes when incomplete
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    // Check if OTP has expired
    if (otpExpired) {
      setError("Code has expired. Please request a new code.");
      // Reset OTP boxes when expired
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);
    try {
      const flowType = location.state?.type || location.state?.flow || "login";
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

      if (flowType === "password-change") {
        console.log("🔐 Verifying password change OTP, method:", passwordVerificationMethod);
        const currentChallenge = passwordVerificationMethod === "email" ? oldOtpChallenge : passwordSmsChallenge;

        if (!currentChallenge) {
          if (passwordVerificationMethod === "email") {
            setError("Verification code has not been sent. Please resend.");
          } else {
            setError("Verification code has not been sent. Please click Send SMS.");
          }
          // Reset OTP boxes when challenge missing
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        if (Date.now() > Number(currentChallenge.expiresAt || 0)) {
          setError("Code has expired. Please request a new code.");
          // Reset OTP boxes when expired
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        if (otp.join("") !== String(currentChallenge.otp)) {
          setError("Incorrect code, please try again");
          // Reset OTP boxes when incorrect
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        // OTP verified - proceed with password change
        try {
          const newPassword = pendingPasswordChange?.newPassword;
          if (!newPassword) {
            setError("Password data not found. Please try again.");
            return;
          }

          // STEP 1: Update password in Firebase Auth
          await updatePassword(auth.currentUser, newPassword);

          // STEP 2: Update hashed password in Firestore (for mobile compatibility)
          const hashedPassword = await hashPassword(newPassword);
          const userRef = doc(db, "users", auth.currentUser.uid);
          
          // Check if current user structure is grouped
          const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
          const isGrouped = userService.isGroupedStructure(storedUser);
          
          const firestoreUpdate = {
            password: hashedPassword,
            updatedAt: serverTimestamp()
          };
          
          if (isGrouped) {
            firestoreUpdate["accountSetup.password"] = hashedPassword;
          }
          
          await updateDoc(userRef, firestoreUpdate);

          // STEP 3: Send password change notification email
          const userEmail = pendingPasswordChange?.email || auth.currentUser.email;
          if (userEmail) {
            try {
              await sendPasswordChangeNotification({ email: userEmail });
            } catch (err) {
              console.warn("Failed to send password change notification:", err);
              // Don't block the flow if notification fails
            }
          }

          // STEP 4: Clear pending data
          localStorage.removeItem('pendingPasswordChange');
          sessionStorage.removeItem(`otp_sent_${auth.currentUser.email}_password-change`);

          // Show success and redirect to login
          setNotification({ 
            message: "Password changed successfully! Logging out...", 
            type: "success" 
          });

          setTimeout(async () => {
            await auth.signOut();
            localStorage.removeItem("user");
            navigate('/login', { 
              state: { message: "Password updated successfully! Please log in with your new password." } 
            });
          }, 2000);

        } catch (err) {
          console.error('Password update failed:', err);
          setError("Failed to update password. Please try again.");
        }
        return;
      }

      if (flowType === "assisted-contact-change") {
        const pendingAssistedChange = JSON.parse(
          localStorage.getItem("pendingAssistedChange") || "null"
        );
        console.log("Pending assisted change:", pendingAssistedChange);
        if (!pendingAssistedChange || !pendingAssistedChange.uid) {
          setError("No pending assisted contact change was found. Please try again.");
          return;
        }

        // STEP 1: Validate token at process start
        const tokenValid = await validateAndRefreshToken();
        if (!tokenValid) {
          setError("Your session has expired. Please log in again to continue.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const authUser = auth.currentUser;
        console.log("Auth current user:", authUser?.uid, authUser?.email);
        
        // CRITICAL: Check authentication before proceeding
        if (!authUser) {
          console.error("User not authenticated");
          setError("Authentication required. Please log in again.");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
          return;
        }

        // STEP 2: Verify email OTP
        console.log("Verifying assisted contact change OTP");
        console.log("Email OTP challenge:", oldOtpChallenge);
        console.log("Entered OTP:", otp.join(""));
        
        if (!oldOtpChallenge) {
          setError("Verification code has not been sent. Please resend.");
          // Reset OTP boxes when challenge missing
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        if (Date.now() > Number(oldOtpChallenge.expiresAt || 0)) {
          setError("Code has expired. Please request a new code.");
          // Reset OTP boxes when expired
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        if (otp.join("") !== String(oldOtpChallenge.otp)) {
          setError("Please Try Again");
          // Reset OTP boxes when incorrect
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
          return;
        }

        // Execute assisted contact update
        await executeAssistedContactUpdate(
          pendingAssistedChange,
          authUser,
          storedUser
        );
        return;
      }

      if (flowType === "assisted-email-change") {
        const pendingAssistedChange = JSON.parse(
          localStorage.getItem("pendingAssistedChange") || "null"
        );
        console.log("Pending assisted email change:", pendingAssistedChange);
        if (!pendingAssistedChange || !pendingAssistedChange.uid) {
          setError("No pending assisted email change was found. Please try again.");
          return;
        }

        // STEP 1: Validate token at process start
        const tokenValid = await validateAndRefreshToken();
        if (!tokenValid) {
          setError("Your session has expired. Please log in again to continue.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const authUser = auth.currentUser;
        console.log("Auth current user:", authUser?.uid, authUser?.email);
        
        // CRITICAL: Check authentication before proceeding
        if (!authUser) {
          console.error("User not authenticated");
          setError("Authentication required. Please log in again.");
          setTimeout(() => {
            navigate("/login");
          }, 2000);
          return;
        }

        // Handle verification based on current step
        if (profileChangeStep === "oldEmail") {
          console.log("Verifying old contact change OTP");
          console.log("Old OTP challenge:", oldOtpChallenge);
          console.log("Entered OTP:", otp.join(""));
          
          if (!oldOtpChallenge) {
            setError("Verification code has not been sent. Please resend.");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (Date.now() > Number(oldOtpChallenge.expiresAt || 0)) {
            setError("Code has expired. Please request a new code.");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (otp.join("") !== String(oldOtpChallenge.otp)) {
            setError("Please Try Again");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          // Move to new email verification
          try {
            const stepToken = await authUser.getIdToken(true);
            console.log("Token refreshed before new email step");

            const payload = JSON.parse(atob(stepToken.split('.')[1]));
            const expiresAt = payload.exp * 1000;
            if (expiresAt - Date.now() < 5 * 60 * 1000) {
              throw new Error("Token expires too soon");
            }
          } catch (error) {
            console.warn("Token refresh failed before new email:", error);
            navigate("/login");
            return;
          }

          setProfileChangeStep("newEmail");
          setIsSendingOtp(true);
          setNotification({ message: "Sending verification code for contact change...", type: "info" });
          
          try {
            await sendOtpToEmail(pendingAssistedChange.currentUserEmail, "assisted-contact-new", setNewOtpChallenge);
            setNotification({ message: "Verification code sent for contact change.", type: "success" });
          } finally {
            setIsSendingOtp(false);
          }
          return;
        }

        if (profileChangeStep === "newEmail") {
          console.log("Verifying new contact change OTP");
          console.log("New OTP challenge:", newOtpChallenge);
          console.log("Entered OTP:", otp.join(""));
          
          if (!newOtpChallenge) {
            setError("Verification code has not been sent to new email yet. Please resend.");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (Date.now() > Number(newOtpChallenge.expiresAt || 0)) {
            setError("Code has expired. Please request a new code.");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (otp.join("") !== String(newOtpChallenge.otp)) {
            setError("Please Try Again");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          // Execute assisted email update
          await executeAssistedEmailUpdate(
            pendingAssistedChange,
            authUser,
            storedUser
          );
          return;
        }

        return;
      }

      if (flowType === "profile-change") {
        const pendingProfileChange = JSON.parse(
          localStorage.getItem("pendingProfileChange") || "null"
        );
        console.log("📋 Pending profile change:", pendingProfileChange);
        if (!pendingProfileChange || !pendingProfileChange.uid) {
          setError("No pending profile change was found. Please try again.");
          return;
        }

        // STEP 1: Validate token at process start
        const tokenValid = await validateAndRefreshToken();
        if (!tokenValid) {
          setError("Your session has expired. Please log in again to continue.");
          setTimeout(() => navigate("/login"), 3000);
          return;
        }

        const authUser = auth.currentUser;
        console.log("👤 Auth current user:", authUser?.uid, authUser?.email);

        // CRITICAL: Check authentication before proceeding
        if (!authUser) {
          console.error("❌ User not authenticated");
          setError("Authentication required. Please log in again.");
          // Redirect to login after a delay
          setTimeout(() => {
            navigate("/login");
          }, 2000);
          return;
        }

        const { uid, originalData = {}, updatedData = {} } = pendingProfileChange;
        
        // FETCH LATEST USER DATA FROM FIRESTORE TO ENSURE CORRECT STRUCTURE
        const latestUser = await userService.getUser(uid);
        const storedUserToUse = latestUser || storedUser;
        const isGrouped = userService.isGroupedStructure(storedUserToUse);
        
        const updatePayload = {};
        
        const emailValue = String(updatedData.email || "").trim().toLowerCase();
        const originalEmailValue = String(originalData.email || "").trim().toLowerCase();
        const isEmailChanging = emailValue && emailValue !== originalEmailValue;

        const originalContactValue = String(originalData.contact || "").replace(/\D/g, "").slice(-10);
        const contactValue = String(updatedData.contact || "").replace(/\D/g, "").slice(-10);
        const isContactChanging = Boolean(contactValue && contactValue !== originalContactValue);

        const usernameValue = updatedData.username?.trim();
        if (usernameValue && usernameValue !== originalData.username) {
          updatePayload.username = usernameValue;
          updatePayload.usernameLower = usernameValue.toLowerCase();
        }

        // BUILD UPDATEPAYLOAD WITH EMAIL/CONTACT CHANGES
        if (isEmailChanging) {
          updatePayload.email = emailValue;
          updatePayload.emailLower = emailValue;
          updatePayload.verified = true;
          if (isGrouped) {
            updatePayload["contactDetails.email"] = emailValue;
          }
          console.log("📧 Email added to updatePayload:", emailValue);
        }

        if (isContactChanging) {
          updatePayload.contact = contactValue;
          updatePayload.phone = `+63${contactValue}`;
          updatePayload.phoneLast10 = contactValue;
          if (isGrouped) {
            updatePayload["contactDetails.contact"] = contactValue;
          }
          console.log("📱 Contact added to updatePayload:", contactValue);
        }

        // Add other profile location fields if changed
        ["region", "province", "city", "barangay"].forEach((field) => {
          if (updatedData[field] !== undefined && updatedData[field] !== originalData[field]) {
            if (isGrouped) {
              updatePayload[`contactDetails.${field}`] = updatedData[field];
            } else {
              updatePayload[field] = updatedData[field];
            }
          }
        });

        if (profileChangeStep === "oldEmail") {
          console.log("📧 Verifying old email or phone OTP, method:", verificationMethod);
          console.log("Old OTP challenge:", oldOtpChallenge);
          console.log("Old SMS OTP challenge:", oldSmsOtpChallenge);
          console.log("Entered OTP:", otp.join(""));
          
          // Determine which challenge to use
          let currentChallenge;
          if (verificationMethod === "email") {
            currentChallenge = oldOtpChallenge;
          } else {
            currentChallenge = oldSmsOtpChallenge;
          }
          
          if (!currentChallenge) {
            if (verificationMethod === "email") {
              setError("Verification code has not been sent. Please resend.");
            } else {
              setError("Verification code has not been sent. Please click Send SMS.");
            }
            // Reset OTP boxes when challenge missing
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (Date.now() > Number(currentChallenge.expiresAt || 0)) {
            setError("Code has expired. Please request a new code.");
            // Reset OTP boxes when expired
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (otp.join("") !== String(currentChallenge.otp)) {
            setError("Please Try Again");
            // Reset OTP boxes when incorrect
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          // Move to next step based on what needs to change
          if (isEmailChanging) {
            // STEP 2: Refresh token before moving to next step
            try {
              const stepToken = await authUser.getIdToken(true);
              console.log("✅ Token refreshed before new email step");

              // Verify token expiry
              const payload = JSON.parse(atob(stepToken.split('.')[1]));
              const expiresAt = payload.exp * 1000;
              if (expiresAt - Date.now() < 5 * 60 * 1000) {
                throw new Error("Token expires too soon");
              }
            } catch (error) {
              console.warn("❌ Token refresh failed before new email:", error);
              // Silent logout without showing error message
              navigate("/login");
              return;
            }

            setProfileChangeStep("newEmail");
            setTimerActive(true);
            setIsSendingOtp(true);
            setNotification({ message: "Sending verification code to your new email...", type: "info" });
            try {
              await sendOtpToEmail(newEmail, "email-change-new", setNewOtpChallenge);
              setNotification({ message: "Verification code sent to your new email.", type: "success" });
            } finally {
              setIsSendingOtp(false);
            }
            return;
          }

          // If contact is changing (and email isn't), move to newPhone step
          if (isContactChanging) {
            // STEP 2: Refresh token before moving to next step
            try {
              const stepToken = await authUser.getIdToken(true);
              console.log("✅ Token refreshed before new phone step");

              // Verify token expiry
              const payload = JSON.parse(atob(stepToken.split('.')[1]));
              const expiresAt = payload.exp * 1000;
              if (expiresAt - Date.now() < 5 * 60 * 1000) {
                throw new Error("Token expires too soon");
              }
            } catch (error) {
              console.warn("❌ Token refresh failed before new phone:", error);
              // Silent logout without showing error message
              navigate("/login");
              return;
            }

            setProfileChangeStep("newPhone");
            setTimerActive(false);
            setHasSmsBeenSent(false);
            setOtp(Array(OTP_LENGTH).fill(""));
            return;
          }

          // Proceed to final commit immediately
          await executeFinalCommit(updatePayload, isEmailChanging, isContactChanging, emailValue, contactValue, updatedData, authUser, uid, storedUserToUse, originalData);
          return;
        }

        
        if (profileChangeStep === "newEmail") {
          console.log("📧 Verifying new email OTP");
          console.log("New OTP challenge:", newOtpChallenge);
          console.log("Entered OTP:", otp.join(""));
          if (!newOtpChallenge) {
            setError("Verification code has not been sent to your new email yet. Please resend.");
            // Reset OTP boxes when challenge missing
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (Date.now() > Number(newOtpChallenge.expiresAt || 0)) {
            setError("Code has expired. Please request a new code.");
            // Reset OTP boxes when expired
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }
          if (otp.join("") !== String(newOtpChallenge.otp)) {
            setError("Please Try Again");
            // Reset OTP boxes when incorrect
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          // If contact is also changing, go to newPhone step
          if (isContactChanging) {
            console.log("📱 Moving to new phone verification step");
            setProfileChangeStep("newPhone");
            setTimerActive(false);
            setHasSmsBeenSent(false);
            setOtp(Array(OTP_LENGTH).fill(""));
            return;
          }

          // All verifications complete, proceed to final commit immediately
          await executeFinalCommit(updatePayload, isEmailChanging, isContactChanging, emailValue, contactValue, updatedData, authUser, uid, storedUserToUse, originalData);
          return;
        }

        if (profileChangeStep === "newPhone") {
          console.log("📱 Verifying new phone OTP");
          console.log("New SMS OTP challenge:", newSmsOtpChallenge);
          console.log("Entered OTP:", otp.join(""));
          if (!newSmsOtpChallenge) {
            setError("Verification code has not been sent to your new phone yet. Please click Send SMS.");
            // Reset OTP boxes when challenge missing
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          if (Date.now() > Number(newSmsOtpChallenge.expiresAt || 0)) {
            setError("Code has expired. Please request a new code.");
            // Reset OTP boxes when expired
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          const enteredCode = otp.join("");
          if (enteredCode !== String(newSmsOtpChallenge.otp)) {
            setError("Please Try Again");
            // Reset OTP boxes when incorrect
            setOtp(Array(OTP_LENGTH).fill(""));
            inputsRef.current[0]?.focus();
            return;
          }

          // All verifications complete, proceed to final commit immediately
          await executeFinalCommit(updatePayload, isEmailChanging, isContactChanging, emailValue, contactValue, updatedData, authUser, uid, storedUserToUse, originalData);
          return;
        }
      }

      if (verificationType === "registration") {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        navigate("/user-home", { state: { user: stored, fromSignup: true } });
      } else if (verificationType === "login") {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        navigate("/user-home", { state: { user: stored } });
      } else if (verificationType === "email-change-step1" || verificationType === "email-change") {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        if (userRole === "admin") {
          navigate("/admin-management", { state: { user: stored } });
        } else {
          navigate("/user-home", { state: { user: stored } });
        }
      } else if (verificationType === "password-change") {
        setNotification({ message: "Password updated.", type: "success" });
        setTimeout(() => {
          if (userRole === "admin") {
            navigate("/admin-management");
          } else {
            navigate("/user-home");
          }
        }, 600);
      }
    } catch (err) {
      console.error("Verification Error:", err);
      setError(err?.message || "Unable to continue. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle resend OTP - UPDATED TO USE RESEND ENDPOINT
  const handleResend = async () => {
    // Prevent multiple clicks while showing "Sent!"
    if (resendText === "Sent!") return;

    // Prevent resend during final commit
    if (profileChangeStep === "finalCommit") return;

    const flowType = location.state?.type || location.state?.flow || "login";

    if (flowType === "password-change") {
      setIsSendingOtp(true);

      try {
        // Invalidate previous OTP
        if (passwordVerificationMethod === "email") {
          setOldOtpChallenge(null);
        } else {
          setPasswordSmsChallenge(null);
        }
        sessionStorage.removeItem(`otp_sent_${location.state?.email}_password-change`);

        if (passwordVerificationMethod === "email") {
          await sendPasswordChangeOtp();
        } else {
          await sendPasswordChangeSmsOtp();
          setPasswordHasSmsBeenSent(true);
        }
        setTimeLeft(60);
        setOtpExpired(false);
        setError("");

        setResendText("Sent!");
        inputsRef.current[0]?.focus();
        setTimeout(() => setResendText("Resend"), 3000);

      } catch (err) {
        setError(err.message || "Failed to resend code.");
      } finally {
        setIsSendingOtp(false);
      }
      return;
    }

    if (flowType === "profile-change") {
      const pendingProfileChange = JSON.parse(
        localStorage.getItem("pendingProfileChange") || "null"
      );
      const currentOldEmail = pendingProfileChange?.currentEmail || pendingProfileChange?.originalData?.email;
      const currentNewEmail = pendingProfileChange?.updatedData?.email;
      const isEmailChanging = Boolean(
        currentOldEmail &&
        currentNewEmail &&
        currentOldEmail.trim().toLowerCase() !== currentNewEmail.trim().toLowerCase()
      );

      try {
        setIsSendingOtp(true);
        if (profileChangeStep === "oldEmail") {
          await sendOtpToEmail(currentOldEmail, "email-change-old", setOldOtpChallenge);
        } else if (profileChangeStep === "newEmail") {
          await sendOtpToEmail(currentNewEmail, "email-change-new", setNewOtpChallenge);
        } else if (profileChangeStep === "newPhone") {
          const currentNewContact = String(pendingProfileChange?.updatedData?.contact || "").replace(/\D/g, "").slice(-10);
          if (!currentNewContact) {
            throw new Error("New phone number is missing.");
          }
          await sendPhoneOtp(currentNewContact);
        }

        setResendText("Sent!");
        setError("");
        inputsRef.current[0]?.focus();
        setTimeout(() => setResendText("Resend"), 3000);
      } catch (sendErr) {
        console.error("Resend OTP failed", sendErr);
        setError(sendErr.message || "Unable to resend OTP. Please try again.");
      } finally {
        setIsSendingOtp(false);
      }

      return;
    }

    if (flowType === "assisted-contact-change") {
      const pendingAssistedChange = JSON.parse(
        localStorage.getItem("pendingAssistedChange") || "null"
      );
      const currentUserEmail = pendingAssistedChange?.updatedData?.email || pendingAssistedChange.email;
      
      if (!currentUserEmail) {
        setError("User email is missing.");
        return;
      }

      try {
        setIsSendingOtp(true);
        await sendOtpToEmail(currentUserEmail, "assisted-contact-change", setOldOtpChallenge);
        setTimeLeft(60);
        setOtpExpired(false);
        setError("");

        setResendText("Sent!");
        inputsRef.current[0]?.focus();
        setTimeout(() => setResendText("Resend"), 3000);
      } catch (sendErr) {
        console.error("Resend OTP failed", sendErr);
        setError(sendErr.message || "Unable to resend OTP. Please try again.");
      } finally {
        setIsSendingOtp(false);
      }
      return;
    }

    if (flowType === "assisted-email-change") {
      const pendingAssistedChange = JSON.parse(
        localStorage.getItem("pendingAssistedChange") || "null"
      );
      
      if (profileChangeStep === "oldEmail") {
        const currentUserEmail = pendingAssistedChange?.currentUserEmail;
        
        if (!currentUserEmail) {
          setError("User email is missing.");
          return;
        }

        try {
          setIsSendingOtp(true);
          await sendOtpToEmail(currentUserEmail, "assisted-contact-old", setOldOtpChallenge);
          setTimeLeft(60);
          setOtpExpired(false);
          setError("");

          setResendText("Sent!");
          inputsRef.current[0]?.focus();
          setTimeout(() => setResendText("Resend"), 3000);
        } catch (sendErr) {
          console.error("Resend OTP failed", sendErr);
          setError(sendErr.message || "Unable to resend OTP. Please try again.");
        } finally {
          setIsSendingOtp(false);
        }
        return;
      }

      if (profileChangeStep === "newEmail") {
        const currentUserEmail = pendingAssistedChange?.currentUserEmail;
        
        if (!currentUserEmail) {
          setError("User email is missing.");
          return;
        }

        try {
          setIsSendingOtp(true);
          await sendOtpToEmail(currentUserEmail, "assisted-contact-new", setNewOtpChallenge);
          setTimeLeft(60);
          setOtpExpired(false);
          setError("");

          setResendText("Sent!");
          inputsRef.current[0]?.focus();
          setTimeout(() => setResendText("Resend"), 3000);
        } catch (sendErr) {
          console.error("Resend OTP failed", sendErr);
          setError(sendErr.message || "Unable to resend OTP. Please try again.");
        } finally {
          setIsSendingOtp(false);
        }
        return;
      }
    }

    try {
      const user = auth.currentUser;
      if (!user) return;
      await sendEmailVerification(user);
      setResendText("Sent!");
      setError("");
      setTimeout(() => setResendText("Resend"), 3000);
    } catch (err) {
      console.error("Resend Error:", err);
      setResendText("Resend");
      setError(err?.message || "Failed to resend. Please try again.");
    }
  };

  return (
    <>
    <div className="verification-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="verification-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="verification-container">
      <div className="verification-card">
        <div id="recaptcha-container" style={{ display: "none" }} />

        {isPasswordChangeFlow ? (
          <>
            <h1 className="verification-title">Verify Password Change</h1>
            <p className="verification-subtitle">
              Enter 6-digit code we sent to your <strong>{passwordVerificationMethod === "sms" ? normalizedPhoneForPassword : (location.state?.email || pendingPasswordChange?.email)}</strong>
            </p>

            <div className={`verification-otp-inputs ${error ? "shake" : ""}`}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={`verification-otp-input ${error && !digit ? "error" : ""}`}
                  disabled={isVerifying}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="verification-error-text">{error}</div>}

            {otpExpired && (
              <div className="verification-error-text">Code has expired. Please request a new one.</div>
            )}

            {passwordTimerActive && (
              <p className="verification-timer-section">
                Code expires in <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
              </p>
            )}

            {/* Resend/Send SMS section for password change */}
            {passwordVerificationMethod === "sms" ? (
              <>
                {!passwordHasSmsBeenSent ? (
                  <button
                    onClick={async () => {
                      setIsSendingOtp(true);
                      try {
                        await sendPasswordChangeSmsOtp();
                        setPasswordHasSmsBeenSent(true);
                        setResendText("Sent!");
                        setTimeout(() => setResendText("Resend"), 3000);
                      } catch (err) {
                        setError(err.message || "Failed to send SMS.");
                      } finally {
                        setIsSendingOtp(false);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3f62b5',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '15px',
                      fontWeight: '600',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      marginTop: '10px',
                      marginBottom: '12px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(63, 98, 181, 0.1)';
                      e.target.style.color = '#29499D';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'none';
                      e.target.style.color = '#3f62b5';
                      e.target.style.transform = 'translateY(0)';
                    }}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </button>
              ) : (
                  <p className="verification-resend-section">
                    Didn't receive a code?{" "}
                    <span 
                      className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                      onClick={handleResend} 
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span>
                  </p>
                )}
              </>
            ) : (
              <p className="verification-resend-section">
                Didn't receive a code?{" "}
                <span className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}>
                  {isSendingOtp ? "Sending..." : resendText}
                </span>
              </p>
            )}

            {/* Try another way button for password change */}
            {normalizedPhoneForPassword && (
              <span className="back-link" onClick={handlePasswordTryAnotherWay} style={{ textDecoration: 'underline' }}>
                Try another way
              </span>
            )}

            <button className="verification-verify-btn" onClick={handleVerify} disabled={!isOtpComplete || isVerifying || otpExpired || (passwordVerificationMethod === "sms" && !passwordHasSmsBeenSent)}>
              {isVerifying ? "Verifying..." : "Verify"}
            </button>

            <button
              className="verification-cancel-btn"
              onClick={() => {
                localStorage.removeItem('pendingPasswordChange');
                navigate('/user-home', { state: { activeTab: 'profile' } });
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <h1 className="verification-title">Enter Verification Code</h1>
            <p className="verification-subtitle">
              Enter 6-digit code we sent to your {profileChangeStep === "newPhone" ? "mobile number" : (profileChangeStep === "oldEmail" && verificationMethod === "sms" ? "mobile number" : "email")}: <strong>{otpDisplayTarget}</strong>
            </p>

            <div className={`verification-otp-inputs ${error ? "shake" : ""}`}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={`verification-otp-input ${error && !digit ? "error" : ""}`}
                  disabled={isVerifying}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="verification-error-text">{error}</div>}

            {otpExpired && (
              <div className="verification-error-text">Code has expired. Please request a new one.</div>
            )}

            {timerActive && (
              <p className="verification-timer-section">
                Code expires in <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>{formatTime(timeLeft)}</span>
              </p>
            )}

            {/* Resend/Send SMS/Email section */}
            {profileChangeStep === "newPhone" ? (
              <p className="verification-resend-section">
                {hasSmsBeenSent ? (
                  <>Didn't receive a code?{" "}
                  <span 
                    className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                    onClick={handleSendNewPhoneSms} 
                    style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : resendText}
                  </span></>
                ) : (
                  <span 
                    className={`verification-resend-link ${isSendingOtp ? "disabled" : ""}`} 
                    onClick={handleSendNewPhoneSms} 
                    style={{ pointerEvents: isSendingOtp ? "none" : "auto", opacity: isSendingOtp ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </span>
                )}
              </p>
            ) : profileChangeStep === "oldEmail" && verificationMethod === "sms" ? (
              <p className="verification-resend-section">
                {hasSmsBeenSent ? (
                  <>Didn't receive a code?{" "}
                  <span 
                    className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                    onClick={handleSendSms} 
                    style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : resendText}
                  </span></>
                ) : (
                  <span 
                    className={`verification-resend-link ${isSendingOtp ? "disabled" : ""}`} 
                    onClick={handleSendSms} 
                    style={{ pointerEvents: isSendingOtp ? "none" : "auto", opacity: isSendingOtp ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </span>
                )}
              </p>
            ) : profileChangeStep === "oldEmail" && verificationMethod === "email" ? (
              <p className="verification-resend-section">
                {hasEmailBeenSent ? (
                  <>Didn't receive a code?{" "}
                  <span 
                    className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                    onClick={handleSendOldEmail} 
                    style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : resendText}
                  </span></>
                ) : (
                  <span 
                    className={`verification-resend-link ${isSendingOtp ? "disabled" : ""}`} 
                    onClick={handleSendOldEmail} 
                    style={{ pointerEvents: isSendingOtp ? "none" : "auto", opacity: isSendingOtp ? 0.5 : 1 }}
                  >
                    {isSendingOtp ? "Sending..." : "Send Email"}
                  </span>
                )}
              </p>
            ) : (
              <p className="verification-resend-section">
                Didn't receive a code?{" "}
                <span 
                  className={`verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                  onClick={profileChangeStep === "oldEmail" ? handleResendOldOtp : handleResend} 
                  style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                >
                  {isSendingOtp ? "Sending..." : resendText}
                </span>
              </p>
            )}

            {/* Try Another Way button (only for oldEmail step) */}
            {profileChangeStep === "oldEmail" && (
              (verificationMethod === "email" && normalizedOldPhone) ||
              (verificationMethod === "sms" && oldEmail)
            ) && (
              <span className="back-link" onClick={handleTryAnotherWay} style={{ textDecoration: "underline" }}>
                Try another way
              </span>
            )}

            <button className="verification-verify-btn" onClick={handleVerify} disabled={isVerifying || !isOtpComplete || otpExpired || isSendingOtp}>
              {isSendingOtp ? "Sending..." : isVerifying ? "Verifying..." : "Verify"}
            </button>

            <button
              className="verification-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>

      <NotificationModal
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      
      <EmailSuccessModal
        show={showEmailSuccessModal}
        onClose={() => setShowEmailSuccessModal(false)}
        onLogin={() => navigate("/login")}
      />
    </>
  );
}
